import { parseMentionTokens } from '@shared/task-contexts';

/**
 * T-8: the markdown people and agents write in task comments, parsed into blocks and inline runs
 * for MentionText to render. A GitHub-comment subset: headings, paragraphs (single line breaks
 * kept, as in chat), bullet, numbered and task lists (nested by indent), quotes, fenced code,
 * rules and tables; inline **bold**, *italic*, ~~struck~~, `code`, [links](url) and \escapes.
 *
 * Mention markup (`@[Sam](user:…)`) and bare web addresses are atomic: they pass through inside
 * `text` runs untouched (so `[Sam](user:…)` isn't a link and `_` in a URL isn't emphasis), and
 * MentionText renders them as before. `#tag` stays a tag: a heading needs a space after its #s.
 */

export type MdInline =
  | { type: 'text'; text: string }
  | { type: 'code'; text: string }
  | { type: 'strong' | 'em' | 'del'; children: MdInline[] }
  | { type: 'link'; href: string; children: MdInline[] }
  /** SF-T-266: `![alt](src)`: shown as a picture where the page can (the agent's messages), else as a link. */
  | { type: 'image'; src: string; alt: string };

export type MdListItem = { checked: boolean | null; blocks: MdBlock[] };

export type MdAlign = 'left' | 'center' | 'right' | null;

export type MdBlock =
  | { type: 'paragraph'; content: MdInline[] }
  | { type: 'heading'; level: number; content: MdInline[] }
  | { type: 'list'; ordered: boolean; start: number; items: MdListItem[] }
  | { type: 'quote'; blocks: MdBlock[] }
  | { type: 'code'; lang: string; text: string }
  | { type: 'rule' }
  | { type: 'table'; align: MdAlign[]; header: MdInline[][]; rows: MdInline[][][] };

const URL_RE = /https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"]/g;
const HEADING_RE = /^ {0,3}(#{1,6})[ \t]+(.*?)(?:[ \t]+#+)?[ \t]*$/;
const RULE_RE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const FENCE_RE = /^( {0,3})(`{3,}|~{3,})[ \t]*([^`\s]*)[^`]*$/;
const LIST_RE = /^( *)([-*+]|\d{1,9}[.)])( +|$)(.*)$/;
const QUOTE_RE = /^ {0,3}> ?(.*)$/;
const TABLE_DELIM_RE = /^ *\|? *:?-+:? *(\| *:?-+:? *)*\|? *$/;
const ESCAPABLE = '\\`*_{}[]()#+-.!|~>';

function isBlank(line: string): boolean {
  return line.trim() === '';
}

function splitRow(line: string): string[] {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|') && !row.endsWith('\\|')) row = row.slice(0, -1);
  const cells: string[] = [];
  let cell = '';
  for (let i = 0; i < row.length; i++) {
    if (row[i] === '\\' && row[i + 1] === '|') {
      cell += '|';
      i++;
    } else if (row[i] === '|') {
      cells.push(cell.trim());
      cell = '';
    } else cell += row[i];
  }
  cells.push(cell.trim());
  return cells;
}

/** A line that starts a block of its own, so it ends the paragraph above it. */
function startsBlock(line: string): boolean {
  return HEADING_RE.test(line) || RULE_RE.test(line) || FENCE_RE.test(line) || QUOTE_RE.test(line) || LIST_RE.test(line);
}

export function parseCommentMarkdown(text: string): MdBlock[] {
  return parseBlocks(text.replace(/\r\n?/g, '\n').split('\n'));
}

function parseBlocks(lines: string[]): MdBlock[] {
  const blocks: MdBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (isBlank(line)) {
      i++;
      continue;
    }

    const fence = FENCE_RE.exec(line);
    if (fence) {
      const [, indent, marks, lang] = fence;
      const body: string[] = [];
      i++;
      while (i < lines.length && !new RegExp(`^ {0,3}${marks[0]}{${marks.length},}[ \\t]*$`).test(lines[i])) {
        body.push(lines[i].startsWith(indent) ? lines[i].slice(indent.length) : lines[i].trimStart());
        i++;
      }
      i++; // the closing fence (or the end)
      blocks.push({ type: 'code', lang, text: body.join('\n') });
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, content: parseInline(heading[2]) });
      i++;
      continue;
    }

    if (RULE_RE.test(line)) {
      blocks.push({ type: 'rule' });
      i++;
      continue;
    }

    if (QUOTE_RE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && !isBlank(lines[i])) {
        const quoted = QUOTE_RE.exec(lines[i]);
        // A line without `>` straight after quoted text carries the quote on (a lazy line).
        if (!quoted && startsBlock(lines[i])) break;
        inner.push(quoted ? quoted[1] : lines[i]);
        i++;
      }
      blocks.push({ type: 'quote', blocks: parseBlocks(inner) });
      continue;
    }

    const list = LIST_RE.exec(line);
    if (list) {
      const [block, next] = parseList(lines, i);
      blocks.push(block);
      i = next;
      continue;
    }

    if (line.includes('|') && i + 1 < lines.length && TABLE_DELIM_RE.test(lines[i + 1])) {
      const header = splitRow(line);
      const align = splitRow(lines[i + 1]).map((cell): MdAlign => {
        const left = cell.startsWith(':');
        const right = cell.endsWith(':');
        return left && right ? 'center' : right ? 'right' : left ? 'left' : null;
      });
      if (align.length === header.length) {
        i += 2;
        const rows: MdInline[][][] = [];
        while (i < lines.length && !isBlank(lines[i]) && lines[i].includes('|')) {
          const cells = splitRow(lines[i]);
          rows.push(header.map((_, c) => parseInline(cells[c] ?? '')));
          i++;
        }
        blocks.push({ type: 'table', align, header: header.map(parseInline), rows });
        continue;
      }
    }

    const para: string[] = [];
    while (i < lines.length && !isBlank(lines[i]) && (para.length === 0 || !startsBlock(lines[i]))) {
      para.push(lines[i].trim());
      i++;
    }
    blocks.push({ type: 'paragraph', content: parseInline(para.join('\n')) });
  }
  return blocks;
}

/** A list from line `from`: items of the same kind, each with the lines indented under it. */
function parseList(lines: string[], from: number): [MdBlock, number] {
  const first = LIST_RE.exec(lines[from])!;
  const ordered = /\d/.test(first[2]);
  const delimiter = first[2].slice(-1);
  const baseIndent = first[1].length;
  const items: MdListItem[] = [];
  let i = from;
  while (i < lines.length) {
    const m = LIST_RE.exec(lines[i]);
    if (!m || m[1].length > baseIndent + 1 || /\d/.test(m[2]) !== ordered || m[2].slice(-1) !== delimiter) break;
    // Content lines under the item line up with its text.
    const contentIndent = m[1].length + m[2].length + Math.min(Math.max(m[3].length, 1), 4);
    const itemLines = [m[4]];
    i++;
    while (i < lines.length) {
      const line = lines[i];
      if (isBlank(line)) {
        // A blank line keeps the item going only if what follows is indented under it.
        let j = i + 1;
        while (j < lines.length && isBlank(lines[j])) j++;
        if (j < lines.length && indentOf(lines[j]) >= contentIndent) {
          itemLines.push('');
          i++;
          continue;
        }
        break;
      }
      const indent = indentOf(line);
      if (indent >= Math.min(contentIndent, baseIndent + 2)) {
        itemLines.push(line.slice(Math.min(indent, contentIndent)));
        i++;
        continue;
      }
      // An unindented line that isn't a new block carries the item's text on (a lazy line).
      if (!startsBlock(line) && !isBlank(itemLines[itemLines.length - 1])) {
        itemLines.push(line.trim());
        i++;
        continue;
      }
      break;
    }
    let checked: boolean | null = null;
    const task = /^\[([ xX])\][ \t]+/.exec(itemLines[0]);
    if (task) {
      checked = task[1] !== ' ';
      itemLines[0] = itemLines[0].slice(task[0].length);
    }
    items.push({ checked, blocks: parseBlocks(itemLines) });
    // Items separated by a blank line are still one list.
    let j = i;
    while (j < lines.length && isBlank(lines[j])) j++;
    if (j > i && j < lines.length && LIST_RE.test(lines[j]) && indentOf(lines[j]) <= baseIndent + 1) i = j;
  }
  return [{ type: 'list', ordered, start: ordered ? parseInt(first[2], 10) : 1, items }, i];
}

function indentOf(line: string): number {
  return line.length - line.trimStart().length;
}

/** Ranges that inline markdown leaves alone: mention markup and bare web addresses. */
function atomicRanges(text: string): Array<{ start: number; end: number }> {
  const ranges = parseMentionTokens(text).map((t) => ({ start: t.start, end: t.end }));
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    // A URL as a markdown link's target, `[x](https://…)`, is the link's, not a bare one.
    if (text.slice(Math.max(0, start - 2), start) === '](') continue;
    if (!ranges.some((r) => start < r.end && end > r.start)) ranges.push({ start, end });
  }
  return ranges.sort((a, b) => a.start - b.start);
}

const isWordChar = (ch: string | undefined) => ch != null && /[\p{L}\p{N}]/u.test(ch);
const isSpace = (ch: string | undefined) => ch == null || /\s/.test(ch);

export function parseInline(text: string): MdInline[] {
  const atomic = atomicRanges(text);
  const atomicAt = new Map(atomic.map((r) => [r.start, r.end]));
  const out: MdInline[] = [];
  let buffer = '';
  const flush = () => {
    if (buffer) out.push({ type: 'text', text: buffer });
    buffer = '';
  };

  /** Where `marker` closes a run that opened before `from`, skipping atomic ranges and code. */
  const findCloser = (marker: string, from: number, intraword: boolean): number => {
    let j = from;
    while (j < text.length) {
      const atomicEnd = atomicAt.get(j);
      if (atomicEnd != null) {
        j = atomicEnd;
        continue;
      }
      if (text[j] === '\\') {
        j += 2;
        continue;
      }
      if (text[j] === '`') {
        const close = text.indexOf('`', j + 1);
        if (close > 0) {
          j = close + 1;
          continue;
        }
      }
      if (text[j] === marker[0]) {
        // A run of the marker's character closes only if it's the same length: `**` doesn't close `*`.
        let run = 1;
        while (text[j + run] === marker[0]) run++;
        if (
          run === marker.length &&
          !isSpace(text[j - 1]) &&
          (intraword || !isWordChar(text[j + marker.length]))
        )
          return j;
        j += run;
        continue;
      }
      j++;
    }
    return -1;
  };

  let i = 0;
  while (i < text.length) {
    const atomicEnd = atomicAt.get(i);
    if (atomicEnd != null) {
      buffer += text.slice(i, atomicEnd);
      i = atomicEnd;
      continue;
    }
    const ch = text[i];

    if (ch === '\\' && ESCAPABLE.includes(text[i + 1] ?? '')) {
      buffer += text[i + 1];
      i += 2;
      continue;
    }

    if (ch === '`') {
      let ticks = 1;
      while (text[i + ticks] === '`') ticks++;
      const fence = '`'.repeat(ticks);
      const close = text.indexOf(fence, i + ticks);
      if (close > 0) {
        flush();
        let code = text.slice(i + ticks, close).replace(/\n/g, ' ');
        if (/^ .*[^ ].* $/.test(code)) code = code.slice(1, -1);
        out.push({ type: 'code', text: code });
        i = close + ticks;
        continue;
      }
      buffer += fence;
      i += ticks;
      continue;
    }

    // [text](url) and ![alt](url); `@[…](…)` that isn't a mention stays text.
    if ((ch === '[' || (ch === '!' && text[i + 1] === '[')) && text[i - 1] !== '@') {
      const open = ch === '!' ? i + 1 : i;
      const link = /^\[((?:[^\][\\]|\\.)*)\]\(\s*<?(https?:\/\/[^\s<>]+?|mailto:[^\s<>]+?|\/[^\s<>]*?)>?(?:\s+"[^"]*")?\s*\)/.exec(text.slice(open));
      if (link) {
        flush();
        const label = link[1] || link[2];
        if (ch === '!') out.push({ type: 'image', src: link[2], alt: link[1].replace(/\\(.)/g, '$1') });
        else out.push({ type: 'link', href: link[2], children: parseInline(label) });
        i = open + link[0].length;
        continue;
      }
    }

    const double = text.slice(i, i + 2);
    if (double === '**' || double === '__' || double === '~~') {
      const intraword = double !== '__';
      if (!isSpace(text[i + 2]) && (intraword || !isWordChar(text[i - 1]))) {
        const close = findCloser(double, i + 2, intraword);
        if (close > i + 2) {
          flush();
          out.push({ type: double === '~~' ? 'del' : 'strong', children: parseInline(text.slice(i + 2, close)) });
          i = close + 2;
          continue;
        }
      }
      buffer += double;
      i += 2;
      continue;
    }

    if (ch === '*' || ch === '_') {
      const intraword = ch === '*';
      if (!isSpace(text[i + 1]) && (intraword || !isWordChar(text[i - 1]))) {
        const close = findCloser(ch, i + 1, intraword);
        if (close > i + 1) {
          flush();
          out.push({ type: 'em', children: parseInline(text.slice(i + 1, close)) });
          i = close + 1;
          continue;
        }
      }
    }

    buffer += ch;
    i++;
  }
  flush();
  return out;
}

/** SF-T-266: a paragraph of pictures only (blanks between them aside): its pictures, else null. */
function picturesOnly(block: MdBlock): { src: string; alt: string }[] | null {
  if (block.type !== 'paragraph') return null;
  const pictures: { src: string; alt: string }[] = [];
  for (const node of block.content) {
    if (node.type === 'image') pictures.push({ src: node.src, alt: node.alt });
    else if (!(node.type === 'text' && !node.text.trim())) return null;
  }
  return pictures.length ? pictures : null;
}

/**
 * SF-T-266: the blocks in order, with several pictures in a row (consecutive paragraphs of pictures only, two or more
 * pictures in all) as one run, to show as a collage. Each run is `blocks[from..to]`; `pictures` is set on a collage's.
 */
export function pictureRuns(blocks: MdBlock[]): { from: number; to: number; pictures: { src: string; alt: string }[] | null }[] {
  const runs: { from: number; to: number; pictures: { src: string; alt: string }[] | null }[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const first = picturesOnly(blocks[i]!);
    if (!first) {
      runs.push({ from: i, to: i, pictures: null });
      continue;
    }
    const pictures = [...first];
    let to = i;
    for (let next = picturesOnly(blocks[to + 1] ?? { type: 'rule' }); next; next = picturesOnly(blocks[to + 1] ?? { type: 'rule' })) {
      pictures.push(...next);
      to++;
    }
    if (pictures.length > 1) {
      runs.push({ from: i, to, pictures });
      i = to;
    } else {
      runs.push({ from: i, to: i, pictures: null });
    }
  }
  return runs;
}
